/**
 * The Medium tier's light sources (lifted from the dungeon-floors prototype's
 * `dungeon-markers.js` marker lights and the fill half of
 * `dungeon-lighting.js`): the exit lanterns, spring glows and boss-room
 * lights, and the full list the bake reads: fixtures, lanterns, marker lights,
 * shaft landings, room stages from the party's spawn, encounter and route
 * bounce, moon pools. Fixed topology and spawn positions, never the camera.
 */
import type { Theme } from '@ab/content-schema';
import type { Floor, Room, Unit } from '@ab/game-core';
import * as THREE from 'three';
import { presentation } from './constants.ts';
import { inRoom, isOpen, orthogonalSteps } from './grid.ts';
import type { Candidate, Dir, Fixture, Lantern, PlanInput, ShaftPlan } from './lighting-plan.ts';
import type { LightSource } from './radiance.ts';

export type MarkerLight = { readonly x: number; readonly height: number; readonly z: number; readonly color: THREE.Color; readonly radius: number; readonly gain: number; readonly kind: 'exit' | 'spring' | 'boss'; readonly tile: number };

const f = presentation.medium.fixtures;
const dirs: readonly Dir[] = orthogonalSteps;

/** Exit lanterns, spring glows, the boss room's corner lights and its dais (`planDungeonMarkerLights`). */
export const markerLights = (floor: Floor, units: readonly Unit[], theme: Theme): MarkerLight[] => {
  const m = f.markers;
  const list: Omit<MarkerLight, 'tile'>[] = [];
  floor.tiles.forEach((t, i) => {
    if (t !== 'exit') return;
    const x = i % floor.width;
    const y = Math.floor(i / floor.width);
    list.push({ x: x + m.exit.dx, height: m.exit.y, z: -y + m.exit.dz, color: new THREE.Color(theme.markers.exit.light), radius: m.exit.radius, gain: m.exit.gain, kind: 'exit' });
  });
  for (const s of floor.springs) list.push({ x: s.pos.x, height: m.spring.y, z: -s.pos.y, color: new THREE.Color(theme.markers.spring.water), radius: m.spring.radius, gain: m.spring.gain, kind: 'spring' });
  const accent = new THREE.Color(theme.bossRoom.accent);
  const occupied = (x: number, y: number): boolean => [...floor.items, ...floor.springs, ...units].some((v) => v.pos.x === x && v.pos.y === y);
  for (const u of units.filter((v) => v.boss !== null)) {
    const r = floor.rooms.find((room) => inRoom(room, u.pos.x, u.pos.y)) ?? floor.rooms[floor.exitRoom];
    if (r === undefined) continue;
    const corners: Dir[] = [
      [r.x, r.y],
      [r.x + r.w - 1, r.y],
      [r.x, r.y + r.h - 1],
      [r.x + r.w - 1, r.y + r.h - 1],
    ];
    corners
      .filter(([x, y], i) => corners.findIndex(([a, b]) => a === x && b === y) === i && isOpen(floor, x, y) && !occupied(x, y))
      .forEach(([cx, cy]) => {
        list.push({ x: cx + (cx === r.x ? -m.boss.inset : m.boss.inset), height: m.boss.y, z: -cy + (cy === r.y ? m.boss.inset : -m.boss.inset), color: accent, radius: m.boss.radius, gain: m.boss.gain, kind: 'boss' });
      });
    if (theme.bossRoom.dais) list.push({ x: u.pos.x, height: m.dais.y, z: -u.pos.y, color: accent, radius: m.dais.radius, gain: m.dais.gain, kind: 'boss' });
  }
  return list.map((l) => ({ ...l, tile: Math.round(-l.z) * floor.width + Math.round(l.x) }));
};

/** The bake's light list: fixtures, lanterns, marker lights, shaft landings, room stages, approach bounce, moon pools. */
export const bakeSources = (input: PlanInput, torches: readonly Fixture[], lanterns: readonly Lantern[], markers: readonly MarkerLight[], shafts: readonly ShaftPlan[], candidates: readonly Candidate[]): LightSource[] => {
  const { floor, units, theme } = input;
  const rules = theme.lightRules;
  const pct = (v: number): number => v / 100;
  const b = f.bake;
  const open = (x: number, y: number): boolean => isOpen(floor, x, y);
  const poolRadius = pct(rules.poolRadiusPercent);
  const gain = pct(rules.gainPercent);
  const vertical = { verticalRadius: pct(rules.torchVerticalRadiusPercent), aboveRadius: pct(rules.torchAboveRadiusPercent) };
  const sconceReach = pct(rules.sconceReachPercent);
  const cool = new THREE.Color(rules.coolFill);
  const out: LightSource[] = [
    ...torches.map((t): LightSource => ({
      x: t.mount === 'wall' ? t.x + t.normal[0] * sconceReach : t.x,
      z: t.mount === 'wall' ? t.z + t.normal[1] * sconceReach : t.z,
      height: f.height,
      color: t.color,
      radius: poolRadius,
      gain,
      core: pct(rules.coreGainPercent),
      coreRadius: pct(rules.coreRadiusPercent),
      ...vertical,
      cool: false,
    })),
    ...lanterns.map((t): LightSource => ({ x: t.x, z: t.z, height: t.height, color: t.color, radius: pct(rules.lanternRadiusPercent), gain: pct(rules.lanternGainPercent), ...vertical, verticalRadius: pct(rules.lanternVerticalRadiusPercent), cool: false })),
    ...markers.map((t): LightSource => ({ x: t.x, z: t.z, height: t.height, color: t.color, radius: t.radius, gain: t.gain, cool: false })),
    ...shafts.map((p): LightSource => ({ x: p.at[0], z: p.at[1], height: b.low, color: new THREE.Color(theme.shafts.color), radius: pct(theme.shafts.landingRadiusPercent), gain: pct(theme.shafts.landingGainPercent), cool: true })),
  ];
  // Stages and bounce follow fixed topology and the party's spawn, never the camera.
  const roomAxis = (r: Room, x: number, y: number): readonly [number, number] => {
    const doors: [number, number][] = [];
    for (let yy = r.y; yy < r.y + r.h; yy += 1) {
      if (open(r.x - 1, yy)) doors.push([r.x, yy]);
      if (open(r.x + r.w, yy)) doors.push([r.x + r.w - 1, yy]);
    }
    for (let xx = r.x; xx < r.x + r.w; xx += 1) {
      if (open(xx, r.y - 1)) doors.push([xx, r.y]);
      if (open(xx, r.y + r.h)) doors.push([xx, r.y + r.h - 1]);
    }
    doors.sort((p, q) => Math.hypot(p[0] - x, p[1] - y) - Math.hypot(q[0] - x, q[1] - y) || p[0] - q[0] || p[1] - q[1]);
    const d = doors[0];
    const length = d === undefined ? 0 : Math.hypot(x - d[0], y - d[1]);
    return d !== undefined && length > 0.01 ? [(x - d[0]) / length, -(y - d[1]) / length] : r.w >= r.h ? [1, 0] : [0, 1];
  };
  floor.rooms.forEach((r, ri) => {
    const party = units.filter((u) => u.side === 'party' && !u.down && inRoom(r, u.pos.x, u.pos.y));
    if (ri === floor.exitRoom && party.length === 0) return;
    const x = party.length > 0 ? party.reduce((sum, u) => sum + u.pos.x, 0) / party.length : r.x + (r.w - 1) / 2;
    const y = party.length > 0 ? party.reduce((sum, u) => sum + u.pos.y, 0) / party.length : r.y + (r.h - 1) / 2;
    const halfLength = Math.min(b.stageLength[1], Math.max(b.stageLength[0], Math.max(r.w, r.h) * b.stageLength[2]));
    const halfWidth = Math.min(b.stageWidth[1], Math.max(b.stageWidth[0], Math.min(r.w, r.h) * b.stageWidth[2]));
    out.push({ x, z: -y, height: b.low, room: r, color: cool, radius: halfLength, gain: pct(rules.coolFillGainPercent) * (party.length > 0 ? 1 : b.emptyStage), verticalRadius: b.fillVertical, stage: { axis: roomAxis(r, x, y), halfLength, halfWidth }, cool: true });
  });
  const approach: LightSource[] = [];
  const addApproach = (x: number, y: number, radius: number, g: number, encounter: boolean): void => {
    if (!open(x, y) || approach.some((p) => Math.hypot(p.x - x, p.z + y) < b.approachSpacing)) return;
    const r = floor.rooms.find((q) => inRoom(q, x, y));
    const openX = Number(open(x - 1, y)) + Number(open(x + 1, y));
    const openY = Number(open(x, y - 1)) + Number(open(x, y + 1));
    const axis = r !== undefined ? roomAxis(r, x, y) : openX >= openY ? ([1, 0] as const) : ([0, 1] as const);
    approach.push({ x, z: -y, height: b.low, ...(encounter && r !== undefined ? { room: r } : {}), color: cool, radius, gain: g, verticalRadius: b.fillVertical, stage: { axis, halfLength: radius, halfWidth: encounter ? b.encounterWidth : b.routeWidth }, cool: true });
  };
  const focal = [...units.filter((u) => u.boss !== null).map((u) => u.pos), ...floor.tiles.flatMap((t, i) => (t === 'exit' ? [{ x: i % floor.width, y: Math.floor(i / floor.width) }] : []))];
  for (const p of focal) {
    const r = floor.rooms.find((q) => inRoom(q, p.x, p.y));
    if (r === undefined) continue;
    const cx = r.x + (r.w - 1) / 2;
    const cy = r.y + (r.h - 1) / 2;
    addApproach(Math.round(p.x + (cx - p.x) * b.encounterToward), Math.round(p.y + (cy - p.y) * b.encounterToward), b.encounterRadius, pct(rules.approachGainPercent) * b.encounterGain, true);
  }
  for (const c of candidates.filter((q) => q.priority < 1)) {
    for (const [dx, dy] of dirs.filter(([ddx, ddy]) => open(c.x + ddx, c.y + ddy)).slice(0, 2)) addApproach(c.x + dx, c.y + dy, b.routeRadius, pct(rules.approachGainPercent), false);
  }
  out.push(...approach);
  if (rules.moonPoolGainPercent > 0) {
    const scale = pct(rules.moonPoolScalePercent);
    for (const r of floor.rooms) {
      out.push({
        x: r.x + (r.w - 1) / 2,
        z: -(r.y + (r.h - 1) / 2),
        height: b.low,
        verticalRadius: b.poolVertical,
        aboveRadius: b.poolAbove,
        room: r,
        color: new THREE.Color(rules.moonPool),
        radius: 1,
        gain: pct(rules.moonPoolGainPercent),
        moonPool: [Math.max(b.poolMin, r.w * 0.5 * scale), Math.max(b.poolMin, r.h * 0.5 * scale)],
        cool: true,
      });
    }
  }
  return out;
};
